/** The slice of an mdast node this plugin reads and writes. */
type MarkdownNode = { type: string; value?: unknown; children?: MarkdownNode[] } & Record<
  string,
  unknown
>;

/** The `<img>` an html node consists of, or null when it holds anything else. */
const loneImage = (html: string): HTMLImageElement | null => {
  const nodes = [...new DOMParser().parseFromString(html, "text/html").body.childNodes].filter(
    (node) => !(node.nodeType === Node.TEXT_NODE && node.textContent?.trim() === ""),
  );
  const [only] = nodes;
  return nodes.length === 1 && only instanceof HTMLImageElement ? only : null;
};

/**
 * Render a lone `<img>` html node as the markdown image it stands for.
 *
 * MDXEditor saves a resized image as `<img width height src />`, and
 * react-markdown shows raw html as escaped text, so a document that falls back
 * from the editor would otherwise print a resized picture as a line of markup.
 * Every other html node stays escaped.
 *
 * The rule must match the server's twin in `api/src/ent/schema/embeddedFiles.ts`:
 * the server lets a shared document's readers load exactly the images it
 * renders, so an image only one side recognises is either broken or reachable
 * without being shown.
 */
export const remarkHtmlImages = () => (tree: MarkdownNode) => {
  const walk = (node: MarkdownNode) => {
    const { children } = node;
    if (!children) return;
    children.forEach((child, index) => {
      const image =
        child.type === "html" && typeof child.value === "string" ? loneImage(child.value) : null;
      const src = image?.getAttribute("src");
      if (!image || src === null || src === undefined) {
        walk(child);
        return;
      }
      const width = image.getAttribute("width");
      const height = image.getAttribute("height");
      children[index] = {
        type: "image",
        url: src,
        alt: image.getAttribute("alt"),
        title: image.getAttribute("title"),
        // The owner's resize; remark-rehype copies these onto the `<img>`.
        data: {
          hProperties: {
            ...(width === null ? {} : { width }),
            ...(height === null ? {} : { height }),
          },
        },
      };
    });
  };
  walk(tree);
};
